import { Box, Button, Typography, Collapse, IconButton, Tooltip, Divider } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { RootError } from './RootError';
import { useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface Props {
  status?: number;
  message?: string;
  stack?: string;
}

export const ServerErrorPage = ({ status, message, stack }: Props) => {
  const { logout } = useAuth0();
  const navigate = useNavigate();
  const [showStack, setShowStack] = useState(false);
  const [copied, setCopied] = useState(false);

  const defaultMessage = "Something went wrong on our end. We're working on fixing it.";
  const displayMessage = message || defaultMessage;

  const handleErrorLogin = () => {
    localStorage.removeItem('accessToken');
    // Note: userInfo is no longer stored in localStorage - it's in RTK Query cache
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleRetry = () => {
    window.location.reload();
  };

  const handleCopyStack = () => {
    if (stack) {
      navigator.clipboard.writeText(stack);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  };

  return (
    <RootError>
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
        {status ? `Error ${status}` : 'Server Error'}
      </Typography>

      <Typography sx={{ mb: 3 }}>{displayMessage}</Typography>

      {stack && (
        <Box
          sx={{
            mb: 3,
            bgcolor: 'background.paper',
            opacity: 0.9,
            borderRadius: 1,
            p: 1,
            border: '1px solid',
            borderColor: 'borderColors.primary',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                Technical Details{' '}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    ml: 0.5,
                    whiteSpace: 'nowrap',
                  }}
                >
                  (data to share with developers)
                </Typography>
              </Typography>
              <IconButton size="small" onClick={() => setShowStack(!showStack)} sx={{ ml: 1 }}>
                {showStack ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            </Box>
            <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
              <IconButton
                size="small"
                onClick={handleCopyStack}
                color={copied ? 'success' : 'default'}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Collapse in={showStack}>
            <Divider sx={{ my: 1 }} />
            <Box
              sx={{
                mt: 1,
                p: 1,
                bgcolor: 'background.paper',
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: '200px',
                fontFamily: 'monospace',
                fontSize: '0.8rem',
              }}
            >
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{stack}</pre>
            </Box>
          </Collapse>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button variant="contained" color="primary" onClick={() => navigate('/')}>
          Go to Dashboard
        </Button>
        <Button variant="outlined" color="primary" onClick={handleRetry}>
          Try Again
        </Button>
        <Button variant="outlined" color="inherit" onClick={handleErrorLogin}>
          Logout
        </Button>
      </Box>
    </RootError>
  );
};
